import supabase from '../supabaseClient';

/** Paid or trialing subscriptions unlock premium (includes 7-day App Store trial) */
const PREMIUM_STATUSES = new Set(['active', 'trialing']);

export interface CurrentSubscription {
  plan_code?: string;
  plan_name?: string;
  status?: string;
  client_limit?: number;
  current_period_end?: string;
  payment_provider?: string;
}

export const isPremiumSubscription = (
  subscription: CurrentSubscription | null | undefined
): boolean => {
  if (!subscription?.status) return false;
  if (!PREMIUM_STATUSES.has(subscription.status)) return false;

  if (subscription.current_period_end) {
    const periodEnd = new Date(subscription.current_period_end);
    if (Number.isNaN(periodEnd.getTime()) || periodEnd <= new Date()) {
      return false;
    }
  }

  return true;
};

export const getCurrentSubscription = async (
  userId: string
): Promise<CurrentSubscription | null> => {
  const { data, error } = await supabase.rpc('get_current_subscription', {
    trainer_uuid: userId,
  });

  if (error) {
    throw error;
  }

  const subscription = Array.isArray(data) ? data[0] : data;
  if (!subscription) return null;

  if (subscription.current_period_end) {
    const periodEnd = new Date(subscription.current_period_end);
    if (Number.isNaN(periodEnd.getTime()) || periodEnd <= new Date()) {
      return null;
    }
  }

  return subscription;
};

export const checkSubscriptionStatus = async (userId: string): Promise<boolean> => {
  try {
    const subscription = await getCurrentSubscription(userId);
    return isPremiumSubscription(subscription);
  } catch (error) {
    console.error('Error checking subscription:', error);
    return false;
  }
};

export const hasLegacyStripeSubscription = async (trainerId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('trainer_subscriptions')
    .select('id')
    .eq('trainer_id', trainerId)
    .eq('payment_provider', 'stripe')
    .in('status', ['active', 'trialing'])
    .limit(1);

  if (error) {
    console.error('Error checking legacy Stripe subscription:', error);
    return false;
  }

  return (data?.length ?? 0) > 0;
};

export const PREMIUM_FEATURES = {
  CLIENT_LIMIT_FREE: 5,
  CLIENT_LIMIT_PREMIUM: 30,
  AUTOMATED_MESSAGING: 'automated_messaging',
  PROFILE_PICTURES: 'profile_pictures',
  ADVANCED_MEAL_ANALYSIS: 'advanced_meal_analysis',
  DIET_CALCULATOR: 'diet_calculator',
};

/** App Store listing — update with your App Store ID once live */
export const APP_STORE_URL = 'https://apps.apple.com/search?term=MacTrack';

export const APPLE_MANAGE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';
